import {
  Button,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EmailIcon from "@mui/icons-material/Email";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SettingsIcon from "@mui/icons-material/Settings";
import PublicIcon from "@mui/icons-material/Public";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import LoginIcon from "@mui/icons-material/Login";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useMutation } from "@tanstack/react-query";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { postWithQueryApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { setUserData } from "../../../redux/MainReducer";
import { store } from "../../../redux/Store";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { errorToast, successToast } from "../../../utils/helper";

// Theme colors
const PRIMARY_COLOR = "#7285b3";
const PRIMARY_DARK = "#5a6a94";
const PRIMARY_LIGHT = "#8fa0c7";

// Styled Components
const PageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  overflowY: "auto",
  backgroundColor: "#f5f6fa",
  boxSizing: "border-box",
  padding: "40px 20px",
});

const SignupCard = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px",
  borderRadius: "16px",
  maxWidth: "450px",
  width: "100%",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
  margin: "auto",
});

const InputWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  marginBottom: "16px",
});

const StyledInput = styled("input")(({ hasError }) => ({
  padding: "14px 20px",
  paddingRight: "45px",
  width: "100%",
  borderRadius: "12px",
  fontSize: "15px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: hasError ? "#d32f2f" : "#e0e0e0",
  fontFamily: "Poppins, sans-serif",
  backgroundColor: "#fafafa",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  "&:focus": {
    outline: "none",
    borderColor: hasError ? "#d32f2f" : PRIMARY_COLOR,
    backgroundColor: "#fff",
    boxShadow: `0 0 0 3px rgba(114, 133, 179, 0.15)`,
  },
  "&:disabled": {
    backgroundColor: "#eee",
    cursor: "not-allowed",
  },
  "&::placeholder": {
    color: "#aaa",
  },
}));

const PasswordToggle = styled(IconButton)({
  position: "absolute",
  right: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#999",
  "&:hover": {
    color: PRIMARY_COLOR,
  },
});

const StyledButton = styled(Button)({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "12px",
  color: "white",
  fontSize: "15px",
  fontWeight: 600,
  backgroundColor: PRIMARY_COLOR,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: PRIMARY_DARK,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px rgba(114, 133, 179, 0.4)`,
  },
  "&:disabled": {
    backgroundColor: "#ccc",
  },
});

const ErrorText = styled("span")({
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
  display: "block",
  paddingLeft: "4px",
});

const SuccessDialogContent = styled(DialogContent)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px 48px",
  textAlign: "center",
  overflowX: "hidden",
});

const IconWrapper = styled("div")(({ bgColor }) => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
  backgroundColor: bgColor || "#e8f5e9",
}));

const OTPContainer = styled(Box)({
  display: "flex",
  gap: "12px",
  justifyContent: "center",
  margin: "24px 0",
});

const OTPInput = styled("input")(({ hasValue, disabled }) => ({
  width: "52px",
  height: "64px",
  fontSize: "28px",
  textAlign: "center",
  border: `2px solid ${hasValue ? PRIMARY_COLOR : "#ccc"}`,
  borderRadius: "12px",
  fontFamily: "Poppins",
  fontWeight: 600,
  transition: "all 0.2s ease",
  backgroundColor: disabled ? "#f5f5f5" : "white",
  cursor: disabled ? "not-allowed" : "text",
  "&:focus": {
    outline: "none",
    borderColor: PRIMARY_DARK,
    boxShadow: `0 0 0 3px rgba(114, 133, 179, 0.2)`,
  },
}));

const ProgressOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10,
  borderRadius: "inherit",
});

const ProgressIconWrapper = styled(Box)(({ isAnimating }) => ({
  width: 100,
  height: 100,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(114, 133, 179, 0.1)",
  marginBottom: 24,
  animation: isAnimating ? "pulse 1.5s ease-in-out infinite" : "none",
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
      boxShadow: `0 0 0 0 rgba(114, 133, 179, 0.4)`,
    },
    "50%": {
      transform: "scale(1.05)",
      boxShadow: `0 0 0 20px rgba(114, 133, 179, 0)`,
    },
  },
}));

const ProgressStep = styled(Box)(({ active, completed }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 16px",
  borderRadius: 10,
  backgroundColor: completed
    ? "rgba(76, 175, 80, 0.1)"
    : active
      ? "rgba(114, 133, 179, 0.1)"
      : "#f5f5f5",
  marginBottom: 8,
  minWidth: 280,
  transition: "all 0.3s ease",
}));

const StepIndicator = styled(Box)(({ active, completed }) => ({
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: completed ? "#4caf50" : active ? PRIMARY_COLOR : "#bdbdbd",
  color: "white",
  fontSize: 14,
  fontWeight: 600,
}));

const ActionCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "14px 16px",
  borderRadius: "12px",
  border: "1px solid #e0e0e0",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: PRIMARY_COLOR,
    backgroundColor: "rgba(114, 133, 179, 0.05)",
    "& .action-icon": {
      color: PRIMARY_COLOR,
    },
    "& .action-text": {
      color: PRIMARY_COLOR,
    },
  },
});

const GoogleSignupModal = ({ children, open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    disableScrollLock={true}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "24px",
        padding: "0px",
        maxWidth: "500px",
        width: "100%",
        overflow: "hidden",
        boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
        position: "relative",
      }
    }}
  >
    {children}
  </Dialog>
);

export const UserForm = ({
  userDetails,
  setUserDetails,
  addNewUser,
  setAddnewUser,
  userListRefetch,
  setSelectedUser,
  seelctedUser,
}) => {
  const cookies = new Cookies();
  const defaultcompany = cookies.get("defaultcompany", "1");
  const navigate = useNavigate();

  // Track touched fields and errors
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // OTP Dialog State
  const [otpDialog, setOtpDialog] = useState({
    open: false,
    email: "",
    isJoiningTenant: false,
  });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  // Verification progress state
  const [verificationProgress, setVerificationProgress] = useState({
    isVerifying: false,
    currentStep: 0,
    steps: [],
  });

  // Success dialog state
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    message: "",
    isPendingApproval: false,
  });

  // Google Signup Modal State
  const [googleSignupModal, setGoogleSignupModal] = useState({
    open: false,
    email: "",
    fullName: "",
    credential: "",
  });
  const [googleDetails, setGoogleDetails] = useState({
    username: "",
    phone: "",
    companyName: "",
    tenantName: "",
  });

  // Timer for resend OTP
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Auto-progress simulation for better UX
  useEffect(() => {
    if (
      verificationProgress.isVerifying &&
      verificationProgress.currentStep < verificationProgress.steps.length - 1
    ) {
      const timer = setTimeout(() => {
        setVerificationProgress((prev) => ({
          ...prev,
          currentStep: prev.currentStep + 1,
        }));
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [verificationProgress.isVerifying, verificationProgress.currentStep]);

  useEffect(() => {
    // Initialize Google GSI for signup
    const initializeGoogleSignup = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleSignupResponse,
        });
        window.google.accounts.id.renderButton(
          document.getElementById("google-signup-button"),
          {
            theme: "outline", // "outline", "filled_blue", "filled_black"
            size: "large", // "small", "medium", "large"
            width: "350",
            text: "signup_with", // "signin_with", "signup_with", "continue_with"
            shape: "rectangular", // "rectangular", "pill", "circle", "square"
            logo_alignment: "center"
          },
        );
      }
    };

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignup;
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleGoogleSignupResponse = (response) => {
    // Decode JWT payload to get email and name
    try {
      const base64Url = response.credential.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      const payload = JSON.parse(jsonPayload);

      setGoogleSignupModal({
        open: true,
        email: payload.email,
        fullName: payload.name,
        credential: response.credential,
      });

      setGoogleDetails((prev) => ({
        ...prev,
        username: payload.email.split("@")[0], // Suggested username
      }));
    } catch (error) {
      console.error("Failed to decode Google token", error);
      errorToast("Failed to process Google account. Please try again.");
    }
  };

  const formHandleChange = (name, value) => {
    setUserDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, userDetails[name]);
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "username":
        if (!value || value.trim() === "") {
          error = "Username is required";
        } else if (value.trim().length < 3) {
          error = "Username must be at least 3 characters";
        }
        break;

      case "title":
        if (!value || value.trim() === "") {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "password":
        if (!value || value === "") {
          error = "Password is required";
        } else if (value.length < 6) {
          error = "Password must be at least 6 characters";
        }
        break;

      case "ip":
        if (!value || value.trim() === "") {
          error = "Network settings is required";
        }
        break;

      case "tenantName":
        if (!value || value.trim() === "") {
          error = "Tenant name is required";
        }
        break;

      case "phone":
        if (!value || value.trim() === "") {
          error = "Phone number is required";
        }
        break;

      case "companyName":
        if (!value || value.trim() === "") {
          error = "Company name is required";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    return error;
  };

  const validateAllFields = () => {
    const fieldsToValidate = [
      "username",
      "title",
      "password",
      "ip",
      "tenantName",
      "phone",
      "companyName",
    ];
    let isValid = true;
    const newErrors = {};
    const newTouched = {};

    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      const error = validateFieldValue(field, userDetails[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);

    return isValid;
  };

  const validateFieldValue = (name, value) => {
    switch (name) {
      case "username":
        if (!value || value.trim() === "") return "Username is required";
        if (value.trim().length < 3)
          return "Username must be at least 3 characters";
        break;
      case "title":
        if (!value || value.trim() === "") return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Please enter a valid email address";
        break;
      case "password":
        if (!value || value === "") return "Password is required";
        if (value.length < 6) return "Password must be at least 6 characters";
        break;
      case "ip":
        if (!value || value.trim() === "")
          return "Network settings is required";
        break;
      case "tenantName":
        if (!value || value.trim() === "") return "Tenant name is required";
        break;
      case "phone":
        if (!value || value.trim() === "") return "Phone number is required";
        break;
      case "companyName":
        if (!value || value.trim() === "") return "Company name is required";
        break;
      default:
        break;
    }
    return "";
  };

  // OTP Input Handlers
  const handleOtpChange = (index, value) => {
    if (verificationProgress.isVerifying) return;
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setOtpError("");

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    if (value && index === 5 && newOtp.every((d) => d !== "")) {
      setTimeout(() => {
        handleVerifyOTPWithProgress(newOtp.join(""));
      }, 150);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (verificationProgress.isVerifying) return;
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      handleVerifyOTP();
    }
  };

  const handleOtpPaste = (e) => {
    if (verificationProgress.isVerifying) return;
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pastedData.length === 0) return;

    const newOtp = ["", "", "", "", "", ""];
    pastedData.split("").forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);

    if (pastedData.length === 6) {
      otpRefs.current[5]?.focus();
      setTimeout(() => {
        handleVerifyOTPWithProgress(pastedData);
      }, 150);
    }
  };

  const maskEmail = (email) => {
    if (!email) return "";
    const [name, domain] = email.split("@");
    if (name.length <= 3) {
      return `${name[0]}***@${domain}`;
    }
    const maskedName = name.slice(0, 2) + "***" + name.slice(-1);
    return `${maskedName}@${domain}`;
  };

  const clearForm = () => {
    setUserDetails({
      username: "",
      title: "",
      password: "",
      ip: "",
      tenantName: "",
    });
    setTouched({});
    setErrors({});
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog({
      open: false,
      message: "",
      isPendingApproval: false,
    });

    navigate(ROUTE_PATHS.LOGIN);
  };

  const handleCloseOtpDialog = () => {
    if (verificationProgress.isVerifying) return;
    setOtpDialog({ open: false, email: "", isJoiningTenant: false });
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setVerificationProgress({ isVerifying: false, currentStep: 0, steps: [] });
  };

  const getVerificationSteps = (isJoiningTenant) => {
    return [
      { label: "Verifying code", icon: "🔐" },
      { label: "Creating tenant", icon: "🏢" },
      { label: "Setting up database", icon: "💾" },
      { label: "Configuring permissions", icon: "🔑" },
      { label: "Finalizing setup", icon: "✨" },
    ];
  };

  // Sign Up Mutation
  const createNewUser = useMutation({
    mutationKey: ["createNewUser"],
    mutationFn: (workData) =>
      postWithQueryApiServices(
        apiRoutes.SIGNUP_USER,
        `selectedCompany=1`,
        workData,
      ),
    onSuccess: async ({ data }) => {
      if (data?.requiresOTPVerification) {
        setOtpDialog({
          open: true,
          email: data.email || userDetails.title,
          isJoiningTenant: data.isJoiningTenant || false,
        });
        setResendTimer(60);
        successToast(data.message || "Verification code sent to your email!");
        return;
      }

      if (data?.pendingApproval) {
        successToast(data?.message || "Account created successfully!");
        clearForm();
        navigate(ROUTE_PATHS.LOGIN);
        return;
      }

      if (data?.token && data?.userId) {
        cookies.set("token", data?.token, { path: "/" });
        cookies.set("userId", data?.userId, { path: "/" });
        cookies.set("userFullName", data?.userFullName, { path: "/" });
        cookies.set("defaultcompany", data?.defaultcompany, { path: "/" });
        cookies.set("tenantId", data?.tenantId, { path: "/" });
        cookies.set("userEmail", data?.userEmail, { path: "/" });
        cookies.set("isTenantAdmin", data?.isTenantAdmin ?? 1, { path: "/" });
        cookies.set("isSystemAdmin", data?.isSystemAdmin ?? 1, { path: "/" });
        cookies.set("activeSubscription", data?.activeSubscription ?? false, {
          path: "/",
        });
        cookies.set("tenantEnabled", data?.tenantEnabled ?? true, {
          path: "/",
        });
        store.dispatch(setUserData({ ...data }));
        successToast(data?.message || "Account created successfully!");
        navigate(ROUTE_PATHS.SUBSCRIBE);
        return;
      }

      if (data?.success && data?.message) {
        setSuccessDialog({
          open: true,
          message: data.message,
          isPendingApproval: false,
        });
        return;
      }

      errorToast("Unexpected response from server. Please try again.");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Signup failed. Please try again.";
      errorToast(errorMessage);
    },
  });

  // Google Signup Mutation
  const googleSignup = useMutation({
    mutationKey: ["googleSignup"],
    mutationFn: (signupData) =>
      postWithQueryApiServices(apiRoutes.googleSignup, "", signupData),
    onSuccess: async ({ data }) => {
      setVerificationProgress((prev) => ({
        ...prev,
        currentStep: prev.steps.length,
      }));

      await new Promise((resolve) => setTimeout(resolve, 800));
      setGoogleSignupModal((prev) => ({ ...prev, open: false }));
      setVerificationProgress({
        isVerifying: false,
        currentStep: 0,
        steps: [],
      });

      if (data?.success || (data?.token && data?.userId)) {
        // Same logic as createNewUser.onSuccess
        if (data?.token && data?.userId) {
          cookies.set("token", data?.token, { path: "/" });
          cookies.set("userId", data?.userId, { path: "/" });
          cookies.set("userFullName", data?.userFullName, { path: "/" });
          cookies.set("defaultcompany", data?.defaultcompany, { path: "/" });
          cookies.set("tenantId", data?.tenantId, { path: "/" });
          cookies.set("userEmail", data?.userEmail, { path: "/" });
          cookies.set("isTenantAdmin", data?.isTenantAdmin ?? 1, { path: "/" });
          cookies.set("isSystemAdmin", data?.isSystemAdmin ?? 1, { path: "/" });
          cookies.set("activeSubscription", data?.activeSubscription ?? false, {
            path: "/",
          });
          cookies.set("tenantEnabled", data?.tenantEnabled ?? true, {
            path: "/",
          });
          store.dispatch(setUserData({ ...data }));
          successToast(
            data?.message ||
              "Account created successfully! Check your email for password.",
          );
          navigate(ROUTE_PATHS.SUBSCRIBE);
        } else if (data?.success && data?.message) {
          setSuccessDialog({
            open: true,
            message: data.message + " Check your email for password.",
            isPendingApproval: false,
          });
        }
      }
    },
    onError: (error) => {
      setVerificationProgress({
        isVerifying: false,
        currentStep: 0,
        steps: [],
      });
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Google Signup failed. Please try again.";
      errorToast(errorMessage);
    },
  });

  // Verify OTP Mutation
  const verifyOTP = useMutation({
    mutationKey: ["verifyOTP"],
    mutationFn: (data) =>
      postWithQueryApiServices(apiRoutes.VERIFY_OTP, "", data),
    onSuccess: async ({ data }) => {
      setVerificationProgress((prev) => ({
        ...prev,
        currentStep: prev.steps.length,
      }));

      await new Promise((resolve) => setTimeout(resolve, 500));

      if (data?.success && data?.token) {
        cookies.set("token", data?.token, { path: "/" });
        cookies.set("userId", data?.userId, { path: "/" });
        cookies.set("userFullName", data?.userFullName, { path: "/" });
        cookies.set("tenantEnabled", data.tenantEnabled ?? true, { path: "/" });
        cookies.set("defaultcompany", data?.defaultcompany, { path: "/" });
        cookies.set("tenantId", data?.tenantId, { path: "/" });
        cookies.set("userEmail", data?.userEmail, { path: "/" });
        cookies.set("isTenantAdmin", data?.isTenantAdmin ?? 1, { path: "/" });
        cookies.set("isSystemAdmin", data?.isSystemAdmin ?? 1, { path: "/" });
        cookies.set("activeSubscription", data?.activeSubscription ?? false, {
          path: "/",
        });
        store.dispatch(setUserData({ ...data }));

        handleCloseOtpDialog();
        clearForm();

        successToast(data?.message || "Tenant created successfully!");
        navigate(ROUTE_PATHS.SUBSCRIBE);
      } else if (data?.success) {
        handleCloseOtpDialog();
        setSuccessDialog({
          open: true,
          message: data?.message || "Account activated successfully!",
          isPendingApproval: false,
        });
        clearForm();
      }
    },
    onError: (error) => {
      setVerificationProgress({
        isVerifying: false,
        currentStep: 0,
        steps: [],
      });

      const errorMessage =
        error?.response?.data?.message || "Invalid OTP. Please try again.";
      setOtpError(errorMessage);

      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();

      if (error?.response?.data?.expired) {
        handleCloseOtpDialog();
        errorToast("OTP has expired. Please sign up again.");
      }
    },
  });

  // Resend OTP Mutation
  const resendOTP = useMutation({
    mutationKey: ["resendOTP"],
    mutationFn: (data) =>
      postWithQueryApiServices(apiRoutes.RESEND_OTP, "", data),
    onSuccess: async ({ data }) => {
      successToast(data?.message || "New verification code sent!");
      setResendTimer(60);
      setOtp(["", "", "", "", "", ""]);
      setOtpError("");
      otpRefs.current[0]?.focus();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to resend OTP.";
      errorToast(errorMessage);

      if (error?.response?.status === 404) {
        handleCloseOtpDialog();
        errorToast("Registration expired. Please sign up again.");
      }
    },
  });

  const handleSubmit = () => {
    const isValid = validateAllFields();

    if (!isValid) {
      errorToast("Please fill all required fields correctly");
      return;
    }

    let workData = {
      ...userDetails,
      email: userDetails.title,
    };

    if (!workData?.defaultcompany) {
      workData.defaultcompany = defaultcompany;
    }

    createNewUser.mutate(workData);
  };

  const handleVerifyOTPWithProgress = (otpString) => {
    if (otpString.length !== 6) {
      setOtpError("Please enter all 6 digits");
      return;
    }

    const steps = getVerificationSteps(otpDialog.isJoiningTenant);
    setVerificationProgress({
      isVerifying: true,
      currentStep: 0,
      steps: steps,
    });

    verifyOTP.mutate({
      email: otpDialog.email,
      otp: otpString,
    });
  };

  const handleVerifyOTP = () => {
    const otpString = otp.join("");
    handleVerifyOTPWithProgress(otpString);
  };

  const handleResendOTP = () => {
    resendOTP.mutate({
      email: otpDialog.email,
    });
  };

  const handleGoogleSignupSubmit = () => {
    // Basic validation
    if (
      !googleDetails.username ||
      !googleDetails.phone ||
      !googleDetails.companyName ||
      !googleDetails.tenantName ||
      !googleDetails.ip
    ) {
      errorToast("Please fill all required fields");
      return;
    }

    const steps = getVerificationSteps(false);
    setVerificationProgress({
      isVerifying: true,
      currentStep: 0,
      steps: steps,
    });

    googleSignup.mutate({
      ...googleDetails,
      email: googleSignupModal.email,
      fullName: googleSignupModal.fullName,
      credential: googleSignupModal.credential,
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <PageContainer>
      <SignupCard elevation={0}>
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
          <Typography
            variant="h5"
            sx={{
              color: PRIMARY_COLOR,
              fontWeight: 700,
              fontFamily: "Poppins",
            }}
          >
            Premierdis
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 600,
            mb: 0.5,
            fontFamily: "Poppins",
            color: "#333",
          }}
        >
          Create Account
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            mb: 3,
            fontFamily: "Poppins",
            color: "#888",
          }}
        >
          Sign up to get started with your tenant
        </Typography>

        {/* Form */}
        <Box sx={{ width: "100%" }}>
          {/* Username */}
          <InputWrapper>
            <StyledInput
              name="username"
              type="text"
              placeholder="Username *"
              value={userDetails?.username || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.username && errors.username}
              disabled={createNewUser.isPending}
            />
            {touched.username && errors.username && (
              <ErrorText>{errors.username}</ErrorText>
            )}
          </InputWrapper>

          {/* Email */}
          <InputWrapper>
            <StyledInput
              name="title"
              type="email"
              placeholder="Email Address *"
              value={userDetails?.title || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.title && errors.title}
              disabled={createNewUser.isPending}
            />
            {touched.title && errors.title && (
              <ErrorText>{errors.title}</ErrorText>
            )}
          </InputWrapper>

          {/* Password */}
          <InputWrapper>
            <StyledInput
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password *"
              value={userDetails?.password || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.password && errors.password}
              disabled={createNewUser.isPending}
            />
            <PasswordToggle
              size="small"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </PasswordToggle>
            {touched.password && errors.password && (
              <ErrorText>{errors.password}</ErrorText>
            )}
          </InputWrapper>

          {/* Network Settings */}
          <InputWrapper>
            <StyledInput
              name="ip"
              type="text"
              placeholder="Network Settings *"
              value={userDetails?.ip || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.ip && errors.ip}
              disabled={createNewUser.isPending}
            />
            {touched.ip && errors.ip && <ErrorText>{errors.ip}</ErrorText>}
          </InputWrapper>

          {/* Phone Number */}
          <InputWrapper>
            <StyledInput
              name="phone"
              type="tel"
              placeholder="Phone Number *"
              value={userDetails?.phone || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.phone && errors.phone}
              disabled={createNewUser.isPending}
            />
            {touched.phone && errors.phone && (
              <ErrorText>{errors.phone}</ErrorText>
            )}
          </InputWrapper>

          {/* Company Name */}
          <InputWrapper>
            <StyledInput
              name="companyName"
              type="text"
              placeholder="Company Name *"
              value={userDetails?.companyName || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.companyName && errors.companyName}
              disabled={createNewUser.isPending}
            />
            {touched.companyName && errors.companyName && (
              <ErrorText>{errors.companyName}</ErrorText>
            )}
          </InputWrapper>

          {/* Tenant Name */}
          <InputWrapper>
            <StyledInput
              name="tenantName"
              type="text"
              placeholder="Tenant Name *"
              value={userDetails?.tenantName || ""}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleBlur(e.target.name)}
              onKeyPress={handleKeyPress}
              hasError={touched.tenantName && errors.tenantName}
              disabled={createNewUser.isPending}
            />
            {touched.tenantName && errors.tenantName && (
              <ErrorText>{errors.tenantName}</ErrorText>
            )}
          </InputWrapper>

          {/* Submit Button */}
          <StyledButton
            variant="contained"
            onClick={handleSubmit}
            disabled={createNewUser.isPending}
            startIcon={!createNewUser.isPending && <PersonAddAltIcon />}
            sx={{ mt: 1 }}
          >
            {createNewUser.isPending ? "Creating Account..." : "Create Account"}
          </StyledButton>
        </Box>

        {/* Divider */}
        <Box
          sx={{ width: "100%", my: 3, display: "flex", alignItems: "center" }}
        >
          <Divider sx={{ flexGrow: 1 }} />
          <Typography
            sx={{
              px: 2,
              color: "#999",
              fontSize: "12px",
              fontFamily: "Poppins",
            }}
          >
            OR
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        {/* Google Signup Button Container */}
        <Box
          sx={{
            width: "100%",
            mb: 3,
            display: "flex",
            justifyContent: "center",
            height: "65px",
            alignItems: "center",
          }}
        >
          <Box
            id="google-signup-button"
            sx={{ transform: "scale(1.31)", transformOrigin: "center" }}
          />
        </Box>

        {/* Login Action Card */}
        <ActionCard
          onClick={() => navigate(ROUTE_PATHS.LOGIN)}
          sx={{ width: "100%" }}
        >
          <LoginIcon
            className="action-icon"
            sx={{ color: "#666", mr: 2, fontSize: 22 }}
          />
          <Box>
            <Typography
              className="action-text"
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#444",
                fontFamily: "Poppins",
              }}
            >
              Already have an account?
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#888",
                fontFamily: "Poppins",
              }}
            >
              Sign in to your existing account
            </Typography>
          </Box>
        </ActionCard>
      </SignupCard>

      {/* OTP Verification Dialog */}
      <Dialog
        open={otpDialog.open}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: 400,
            maxWidth: 500,
            width: "100%",
          },
        }}
      >
        {/* Progress Overlay */}
        {verificationProgress.isVerifying && (
          <ProgressOverlay>
            <ProgressIconWrapper isAnimating={true}>
              {otpDialog.isJoiningTenant ? (
                <SettingsIcon sx={{ fontSize: 48, color: PRIMARY_COLOR }} />
              ) : (
                <RocketLaunchIcon sx={{ fontSize: 48, color: PRIMARY_COLOR }} />
              )}
            </ProgressIconWrapper>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "#333",
                fontFamily: "Poppins",
              }}
            >
              {otpDialog.isJoiningTenant
                ? "Processing Your Request"
                : "Creating Your Tenant"}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#666", mb: 3, fontFamily: "Poppins" }}
            >
              Please wait, this may take a few moments...
            </Typography>

            {/* Progress Steps */}
            <Box sx={{ width: "100%", maxWidth: 340 }}>
              {verificationProgress.steps.map((step, index) => {
                const isCompleted = index < verificationProgress.currentStep;
                const isActive = index === verificationProgress.currentStep;

                return (
                  <ProgressStep
                    key={index}
                    active={isActive}
                    completed={isCompleted}
                  >
                    <StepIndicator active={isActive} completed={isCompleted}>
                      {isCompleted ? "✓" : step.icon}
                    </StepIndicator>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 400,
                        color: isCompleted
                          ? "#4caf50"
                          : isActive
                            ? PRIMARY_COLOR
                            : "#999",
                        fontFamily: "Poppins",
                      }}
                    >
                      {step.label}
                      {isActive && !isCompleted && "..."}
                    </Typography>
                    {isActive && !isCompleted && (
                      <CircularProgress
                        size={16}
                        sx={{ ml: "auto", color: PRIMARY_COLOR }}
                      />
                    )}
                  </ProgressStep>
                );
              })}
            </Box>

            {/* Progress Bar */}
            <Box sx={{ width: "100%", maxWidth: 320, mt: 3 }}>
              <LinearProgress
                variant="determinate"
                value={
                  (verificationProgress.currentStep /
                    verificationProgress.steps.length) *
                  100
                }
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    backgroundColor: PRIMARY_COLOR,
                  },
                }}
              />
            </Box>
          </ProgressOverlay>
        )}

        {/* OTP Form Content */}
        <Box
          sx={{
            opacity: verificationProgress.isVerifying ? 0 : 1,
            transition: "opacity 0.3s ease",
            pointerEvents: verificationProgress.isVerifying ? "none" : "auto",
          }}
        >
          <SuccessDialogContent>
            <IconWrapper bgColor="rgba(114, 133, 179, 0.1)">
              <EmailIcon sx={{ fontSize: 40, color: PRIMARY_COLOR }} />
            </IconWrapper>

            <DialogTitle
              sx={{
                padding: 0,
                marginBottom: 1,
                fontSize: "1.5rem",
                fontWeight: 600,
                fontFamily: "Poppins",
              }}
            >
              Verify Your Email
            </DialogTitle>

            <DialogContentText
              sx={{
                fontSize: "1rem",
                color: "#555",
                lineHeight: 1.6,
                mb: 1,
                fontFamily: "Poppins",
              }}
            >
              We've sent a 6-digit verification code to
            </DialogContentText>

            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: PRIMARY_COLOR,
                mb: 2,
                fontFamily: "Poppins",
              }}
            >
              {maskEmail(otpDialog.email)}
            </Typography>

            {/* Info Box */}
            <Box
              sx={{
                backgroundColor: "rgba(114, 133, 179, 0.08)",
                borderRadius: 2,
                p: 1.5,
                mb: 2,
                width: "100%",
                border: `1px solid rgba(114, 133, 179, 0.2)`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: PRIMARY_DARK, fontFamily: "Poppins" }}
              >
                {otpDialog.isJoiningTenant
                  ? "After verification, your request will be sent for admin approval."
                  : "After verification, your tenant will be created automatically."}
              </Typography>
            </Box>

            <OTPContainer onPaste={handleOtpPaste}>
              {otp.map((digit, index) => (
                <OTPInput
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  hasValue={!!digit}
                  disabled={verificationProgress.isVerifying}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  autoFocus={index === 0}
                />
              ))}
            </OTPContainer>

            {otpError && (
              <Typography
                color="error"
                sx={{ mb: 2, fontSize: "0.875rem", fontFamily: "Poppins" }}
              >
                {otpError}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontFamily: "Poppins" }}
              >
                Didn't receive the code?
              </Typography>
              {resendTimer > 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: PRIMARY_COLOR,
                    fontWeight: 500,
                    fontFamily: "Poppins",
                  }}
                >
                  Resend in {formatTime(resendTimer)}
                </Typography>
              ) : (
                <Button
                  size="small"
                  onClick={handleResendOTP}
                  disabled={
                    resendOTP.isPending || verificationProgress.isVerifying
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: PRIMARY_COLOR,
                    fontFamily: "Poppins",
                  }}
                >
                  {resendOTP.isPending ? "Sending..." : "Resend Code"}
                </Button>
              )}
            </Box>

            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mb: 1, fontFamily: "Poppins" }}
            >
              Code expires in 10 minutes
            </Typography>
          </SuccessDialogContent>

          <DialogActions
            sx={{
              justifyContent: "center",
              paddingBottom: 3,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={handleCloseOtpDialog}
              variant="outlined"
              disabled={verificationProgress.isVerifying}
              sx={{
                borderRadius: "12px",
                padding: "10px 30px",
                minWidth: 120,
                borderColor: "#e0e0e0",
                color: "#666",
                fontFamily: "Poppins",
                textTransform: "none",
                "&:hover": {
                  borderColor: PRIMARY_COLOR,
                  color: PRIMARY_COLOR,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerifyOTP}
              variant="contained"
              disabled={
                verifyOTP.isPending ||
                otp.join("").length !== 6 ||
                verificationProgress.isVerifying
              }
              sx={{
                borderRadius: "12px",
                padding: "10px 40px",
                backgroundColor: PRIMARY_COLOR,
                minWidth: 160,
                fontFamily: "Poppins",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: PRIMARY_DARK,
                },
              }}
            >
              {verifyOTP.isPending || verificationProgress.isVerifying ? (
                <CircularProgress size={24} color="inherit" />
              ) : otpDialog.isJoiningTenant ? (
                "Verify & Submit"
              ) : (
                "Verify & Create"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Success/Pending Approval Dialog */}
      <Dialog
        open={successDialog.open}
        onClose={handleCloseSuccessDialog}
        aria-labelledby="success-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
          },
        }}
      >
        <SuccessDialogContent>
          <IconWrapper
            bgColor={successDialog.isPendingApproval ? "#fff3e0" : "#e8f5e9"}
          >
            {successDialog.isPendingApproval ? (
              <HourglassEmptyIcon sx={{ fontSize: 40, color: "#e65100" }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#2e7d32" }} />
            )}
          </IconWrapper>

          <DialogTitle
            id="success-dialog-title"
            sx={{
              padding: 0,
              marginBottom: 1,
              fontSize: "1.5rem",
              fontWeight: 600,
              fontFamily: "Poppins",
            }}
          >
            {successDialog.isPendingApproval
              ? "Request Submitted!"
              : "Success!"}
          </DialogTitle>

          <DialogContentText
            sx={{
              fontSize: "1rem",
              color: "#555",
              lineHeight: 1.6,
              fontFamily: "Poppins",
            }}
          >
            {successDialog.message}
          </DialogContentText>
        </SuccessDialogContent>

        <DialogActions sx={{ justifyContent: "center", paddingBottom: 3 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            sx={{
              borderRadius: "12px",
              padding: "10px 40px",
              fontFamily: "Poppins",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: successDialog.isPendingApproval
                ? "#e65100"
                : "#2e7d32",
              "&:hover": {
                backgroundColor: successDialog.isPendingApproval
                  ? "#bf360c"
                  : "#1b5e20",
              },
            }}
          >
            {successDialog.isPendingApproval ? "Go to Login" : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>
      <GoogleSignupModal
        open={googleSignupModal.open}
        onClose={() =>
          !verificationProgress.isVerifying &&
          setGoogleSignupModal((prev) => ({ ...prev, open: false }))
        }
      >
        {/* Progress Overlay (Same as OTP) */}
        {verificationProgress.isVerifying && (
            <Box
              sx={{
                padding: "40px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
                minHeight: "450px",
              }}
            >
            <ProgressIconWrapper isAnimating={true}>
              <RocketLaunchIcon sx={{ fontSize: 50, color: PRIMARY_COLOR }} />
            </ProgressIconWrapper>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1,
                fontFamily: "Poppins",
                color: PRIMARY_COLOR,
              }}
            >
              Setting Up Your Tenant
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#666", mb: 3, fontFamily: "Poppins" }}
            >
              Please wait, this may take a few moments...
            </Typography>

            {/* Progress Steps */}
            <Box sx={{ width: "100%", maxWidth: "340px", mx: "auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
              {verificationProgress.steps.map((step, index) => {
                const isCompleted = index < verificationProgress.currentStep;
                const isActive = index === verificationProgress.currentStep;

                return (
                  <ProgressStep
                    key={index}
                    active={isActive}
                    completed={isCompleted}
                    sx={{ minWidth: "unset", width: "100%" }}
                  >
                    <StepIndicator active={isActive} completed={isCompleted}>
                      {isCompleted ? "✓" : step.icon}
                    </StepIndicator>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 400,
                        color: isCompleted
                          ? "#4caf50"
                          : isActive
                            ? PRIMARY_COLOR
                            : "#999",
                        fontFamily: "Poppins",
                      }}
                    >
                      {step.label}
                      {isActive && !isCompleted && "..."}
                    </Typography>
                    {isActive && !isCompleted && (
                      <CircularProgress
                        size={16}
                        sx={{ ml: "auto", mr: 1, color: PRIMARY_COLOR }}
                      />
                    )}
                  </ProgressStep>
                );
              })}
            </Box>

            {/* Progress Bar */}
            <Box sx={{ width: "100%", maxWidth: "360px", mt: 3, mx: "auto", alignSelf: "center" }}>
              <LinearProgress
                variant="determinate"
                value={
                  (verificationProgress.currentStep /
                    verificationProgress.steps.length) *
                  100
                }
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    backgroundColor: PRIMARY_COLOR,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: verificationProgress.isVerifying ? "none" : "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: "30px",
            transition: "all 0.3s ease",
          }}
        >
          <IconWrapper bgColor="rgba(114, 133, 179, 0.1)">
            <PersonAddAltIcon sx={{ fontSize: 40, color: PRIMARY_COLOR }} />
          </IconWrapper>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 1, fontFamily: "Poppins" }}
          >
            Complete Your Profile
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#666", mb: 4, fontFamily: "Poppins" }}
          >
            We've got your email from Google. Please provide a few more details
            to set up your account.
          </Typography>

          <Box sx={{ width: "100%" }}>
            <InputWrapper>
              <StyledInput
                type="email"
                value={googleSignupModal.email}
                disabled={true}
                placeholder="Email Address"
              />
            </InputWrapper>

            <InputWrapper>
              <StyledInput
                name="username"
                type="text"
                placeholder="Username *"
                value={googleDetails.username}
                onChange={(e) =>
                  setGoogleDetails((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
              />
            </InputWrapper>

            <InputWrapper>
              <StyledInput
                name="ip"
                type="text"
                placeholder="Network Settings *"
                value={googleDetails.ip || ""}
                onChange={(e) =>
                  setGoogleDetails((prev) => ({ ...prev, ip: e.target.value }))
                }
              />
            </InputWrapper>

            <InputWrapper>
              <StyledInput
                name="phone"
                type="tel"
                placeholder="Phone Number *"
                value={googleDetails.phone}
                onChange={(e) =>
                  setGoogleDetails((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
              />
            </InputWrapper>

            <InputWrapper>
              <StyledInput
                name="companyName"
                type="text"
                placeholder="Company Name *"
                value={googleDetails.companyName}
                onChange={(e) =>
                  setGoogleDetails((prev) => ({
                    ...prev,
                    companyName: e.target.value,
                  }))
                }
              />
            </InputWrapper>

            <InputWrapper>
              <StyledInput
                name="tenantName"
                type="text"
                placeholder="Tenant Name *"
                value={googleDetails.tenantName}
                onChange={(e) =>
                  setGoogleDetails((prev) => ({
                    ...prev,
                    tenantName: e.target.value,
                  }))
                }
              />
            </InputWrapper>

            <StyledButton
              variant="contained"
              onClick={handleGoogleSignupSubmit}
              disabled={googleSignup.isPending}
              sx={{ mt: 2 }}
            >
              {googleSignup.isPending ? "Setting up..." : "Complete Signup"}
            </StyledButton>

            <Button
              fullWidth
              onClick={() =>
                setGoogleSignupModal((prev) => ({ ...prev, open: false }))
              }
              sx={{
                mt: 1,
                color: "#666",
                textTransform: "none",
                fontFamily: "Poppins",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </GoogleSignupModal>
    </PageContainer>
  );
};
